/**
 * Bill Runs (PRD 2026-10-21, US-009): the four groups of metric cards.
 *
 * Each group is counted over the rows that pass every condition except the
 * group's own, so its cards sum to the group's total and clicking one never
 * empties its neighbours (the Bulk Bill Run chips work this way):
 *
 *   Coverage  per month — Expected · Has bill · Missing · Covered · Not expected   (skips Coverage)
 *   Outcome   Matched · Undercharge · Overcharge · No bill yet · Awaiting validation  (skips Status, Status Details)
 *   Assigned  one card per person-set step                                         (skips Assigned)
 *   Send      No report · Not sent · Sent · Send failed · Changed since sent        (skips Send)
 *   Progress  one card per bill status, Missing Invoice … Completed               (skips Workflow)
 *
 * Counts only: a month's summed variance hides the bills behind it, so the
 * variance is read per bill (table column, Variance filter), never totalled.
 */
import { WORKFLOW_ORDER, type BillWorkflowStatus, type WorkflowStep } from './billWorkflow';
import type { BillRunRow } from './billRunRows';
import { isNeverBilled, matchesBillRunConditions, type BillRunConditions, type ConditionKey, type MatchOptions } from './billRunConditions';
import { ASSIGNED_ORDER, billRunStatus } from './billRunStatus';
import type { ReportSendStatus } from './reportStatus';

export type CoverageCounts = { expected: number; hasBill: number; missing: number; covered: number; notExpected: number };
export type MonthCoverage = CoverageCounts & { month: string };

export type OutcomeCounts = {
  matched: number;
  undercharge: number;
  overcharge: number;
  /** Expected site-months with no bill (coverage Missing). Not bills. */
  noBill: number;
  /** Bills with no verdict yet (status Pending). */
  awaitingValidation: number;
  /** Mismatched, not yet classified under or over. */
  mismatchPending: number;
};

export type BillRunMetrics = {
  /** `neverBilled`: the Missing site-months whose site has never had a bill (part of `total.missing`). */
  coverage: { total: CoverageCounts; byMonth: MonthCoverage[]; neverBilled: number };
  outcome: OutcomeCounts;
  assigned: Record<WorkflowStep, number>;
  send: Record<ReportSendStatus, number>;
  /** Bills at each status (lib/billWorkflow). */
  progress: Record<BillWorkflowStatus, number>;
};

const emptyCoverage = (): CoverageCounts => ({ expected: 0, hasBill: 0, missing: 0, covered: 0, notExpected: 0 });

/** Group keys → the conditions that group leaves out. */
const OWN: Record<'coverage' | 'outcome' | 'assigned' | 'send' | 'progress', ConditionKey[]> = {
  coverage: ['coverage', 'neverBilled'],
  outcome: ['status', 'details'],
  assigned: ['assigned'],
  send: ['send'],
  progress: ['workflow'],
};

export function computeMetrics(
  rows: readonly BillRunRow[],
  conditions: BillRunConditions,
  opts: Pick<MatchOptions, 'runBillIds' | 'driverBillIds'> = {},
): BillRunMetrics {
  const pass = (group: keyof typeof OWN) =>
    rows.filter((r) => matchesBillRunConditions(r, conditions, { ...opts, skip: OWN[group] }));

  // Coverage: one count per expected site-month. Two bills in one month are one
  // "Has bill"; an archived bill never covers; a bill on a site not expected
  // that month is not part of coverage.
  const byMonth = new Map<string, MonthCoverage>();
  const at = (month: string) => {
    let m = byMonth.get(month);
    if (!m) byMonth.set(month, (m = { month, ...emptyCoverage() }));
    return m;
  };
  const billed = new Set<string>();
  let neverBilled = 0;
  for (const r of pass('coverage')) {
    if (isNeverBilled(r)) neverBilled += 1;
    if (r.kind === 'bill') {
      if (r.archived || !r.expected || billed.has(`${r.siteId}|${r.month}`)) continue;
      billed.add(`${r.siteId}|${r.month}`);
      at(r.month).hasBill += 1;
    } else if (r.coverage === 'missing') at(r.month).missing += 1;
    else if (r.coverage === 'covered') at(r.month).covered += 1;
    else at(r.month).notExpected += 1;
  }
  const months = [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
  const total = emptyCoverage();
  for (const m of months) {
    m.expected = m.hasBill + m.missing + m.covered + m.notExpected;
    total.expected += m.expected;
    total.hasBill += m.hasBill;
    total.missing += m.missing;
    total.covered += m.covered;
    total.notExpected += m.notExpected;
  }

  // Outcome: bills and missing months; a covered or not-expected month is not
  // an outcome. Nor is a bill on a site not enrolled during its period
  // (Cancelled earlier, Pending Enrolment…): validating it is not the service, so it would only
  // pad the match rate's denominator. It still shows in the table and the
  // workflow cards, where someone may need to act on it.
  const outcome: OutcomeCounts = {
    matched: 0, undercharge: 0, overcharge: 0, noBill: 0, awaitingValidation: 0, mismatchPending: 0,
  };
  for (const r of pass('outcome')) {
    if (r.kind === 'missing') {
      if (r.coverage === 'missing') outcome.noBill += 1;
      continue;
    }
    if (!r.inScope) continue;
    const s = billRunStatus(r);
    if (s.status === 'matched') outcome.matched += 1;
    else if (s.status === 'pending') outcome.awaitingValidation += 1;
    else if (s.details === 'overcharge') outcome.overcharge += 1;
    else if (s.details === 'undercharge') outcome.undercharge += 1;
    else outcome.mismatchPending += 1;
  }

  // Assigned and Send: bills only (a missing month has neither).
  const assigned = Object.fromEntries(ASSIGNED_ORDER.map((s) => [s, 0])) as Record<WorkflowStep, number>;
  for (const r of pass('assigned')) {
    if (r.kind !== 'bill') continue;
    const a = billRunStatus(r).assigned;
    if (a) assigned[a] += 1;
  }
  const send: Record<ReportSendStatus, number> = {
    no_report: 0, not_sent: 0, sent: 0, send_failed: 0, changed_since_sent: 0,
  };
  for (const r of pass('send')) {
    if (r.kind === 'bill') send[billRunStatus(r).send] += 1;
  }

  const progress = Object.fromEntries(WORKFLOW_ORDER.map((s) => [s, 0])) as Record<BillWorkflowStatus, number>;
  for (const r of pass('progress')) {
    if (r.kind === 'bill') progress[r.board.status] += 1;
  }

  return { coverage: { total, byMonth: months, neverBilled }, outcome, assigned, send, progress };
}
